import React, { useContext } from 'react'

import { Link } from 'react-router-dom'
import useStructureParams from '../lib/useStructureParams'
import { filterAndSortChildren, wardNamesCleanup } from '../lib/structures'
import { useRegistration } from '../data/useRegistration'

import PageTitle from './PageTitle'
import Breadcrumbs from './Breadcrumbs'
import PageHeader from './PageHeader'
import ViewButtons from './ViewButtons'
import ResponsiblePersons from './ResponsiblePersons'

import Table from 'react-bootstrap/Table'
import { Col, Row } from 'react-bootstrap'

import Map from './Map'

import { LoadingSpinner } from './spinners'
import { NoAssignedWardsMessage } from './alerts'
import { statsStyle, formatInt, formatPerc } from '../lib/utils'

import {
  Chart as ChartJS,
  registerables
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import annotationPlugin from 'chartjs-plugin-annotation'

import { Line } from 'react-chartjs-2'
import VDCategoryFilter from './VDCategoryFilter'
import { DashboardContext } from '../data/context'
import HeaderLayout from "./HeaderLayout"

ChartJS.register(annotationPlugin, ...registerables)

export default function RegistrationView (): JSX.Element {
  const { filterLabel } = useContext(DashboardContext)
  // get parameters from the URL
  const { code, type } = useStructureParams()

  // get stats from the GraphQL API
  const { stats, loading } = useRegistration({ type, code, filterLabel })

  // this happens while the query is still fetching
  if (stats == null || loading) {
    return <LoadingSpinner />
  }

  // filter/sort children appropriately according to structure type
  const children = filterAndSortChildren(stats.type, stats.children)
    .map(wardNamesCleanup)
    .filter(c => c.structureHasAssignedWards)
    .sort((a, b) => {
      const getProgress = (compare: typeof a) => compare.registration.target ? compare.registration.rollAll / compare.registration.target : -1
      const progressDiff = getProgress(b) - getProgress(a)

      if (progressDiff !== 0) return progressDiff
      return (a.name ?? '').localeCompare(b.name ?? '')
    })

  const mapColor = (code: string): string => {
    const c = children.find(c => c.code === code)
    if (c == null) return '#ffffff'
    const color = statsStyle(c.registration.rollAll, c.registration.milestone).backgroundColor
    return color
  }

  const historicRollLine = stats.registration.historicRoll.map(datum => ({ x: datum.date, y: datum.count }))
  const milestonesLine = stats.registration.milestones.map(datum => ({ x: datum.date, y: datum.count }))

  console.log(milestonesLine)

  const graphData = {
    datasets: [
      {
        label: 'Registrations per IEC',
        data: historicRollLine,
        borderColor: '#4D77FF'
      },
      {
        label: 'Registration Worm',
        data: milestonesLine,
        borderColor: '#56BBF1',
        borderDash: [2, 2],
        pointRadius: 0
      }
    ]
  }

  const targetAnnotations = stats.registration.target
    ? {
        target: {
          type: 'line',
          yMin: stats.registration.target,
          yMax: stats.registration.target,
          borderColor: 'rgb(255, 99, 132)',
          borderWidth: 2,
          label: {
            enabled: true,
            content: 'Target'
          }
        }
      }
    : null

  const graphOptions = {
    interaction: {
      intersect: false,
      mode: 'index'
    },
    animation: false,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'day',
          tooltipFormat: 'd LLL yyyy'
        }
      },
      y: {
        beginAtZero: true
      }
    },
    plugins: {
      autocolors: false,
      annotation: {
        annotations: targetAnnotations
      }
    }
  }

  if (!stats.structureHasAssignedWards) {
    return (
      <>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} suffix='/registration' />
        {/* <PageHeader structure={stats} />
        <VDCategoryFilter counts={stats.counts} />
        <ViewButtons structure={stats} section='registration' /> */}
        <HeaderLayout
           element1={<PageHeader structure={stats} />}
           element2={<ViewButtons structure={stats} section="registration" />}
           element3={<VDCategoryFilter counts={stats.counts} />}
        />
        <NoAssignedWardsMessage type={stats.type} />
      </>
    )
  } else {
    return (
      <>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} suffix='/registration' />
        {/* <PageHeader structure={stats} />
        <VDCategoryFilter counts={stats.counts} />
        <ViewButtons structure={stats} section='registration' /> */}
        <HeaderLayout
           element1={<PageHeader structure={stats} />}
           element2={<ViewButtons structure={stats} section="registration" />}
           element3={<VDCategoryFilter counts={stats.counts} />}
        />

        <Row>
          <Col md={6}>
            <div style={{ position: 'relative', height: '400px', width: '100%' }}>
              {/* @ts-expect-error */}
              <Line options={graphOptions} data={graphData} />

            </div>
          </Col>
          <Col md={6}>
            <Map type={type} code={code} childStructures={stats.children} childColourFn={mapColor} />
          </Col>
        </Row>

        <Table responsive striped size='sm' className='break-before stats'>
          <thead>
            <tr>
              <th />
              <th className='text-center'>VD Counts</th>
              <th className='text-center'>DA&nbsp;Voters confirmed as registered (since 1 June 2024)</th>
              <th className='text-center'>Increase in DA&nbsp;Voters confirmed as registered <br /> (last 7 days) </th>
              <th className='text-center'>Increase in DA&nbsp;Voters confirmed as registered <br /> (last 30 days) </th>
              <th className='text-center'>Weekly Average DA&nbsp;Voters confirmed as registered <br /> (last 6 weeks) </th>
              <th className='text-center'>Weekly Average DA&nbsp;voters confirmed as registered required <br /> (to reach target) </th>
              <th className='text-center'>Registrations we should have done by now </th>
              <th className='text-center'>Registrations as a % of where we should be</th>
              <th className='text-center'>Registration Target</th>
              <th className='text-center'>% Target Success</th>
            </tr>
          </thead>
          <tbody>
            {children.map(c => (
              <tr key={c.code}>
                {stats.type !== 'ward' ? <td><Link to={`/${c.type}/${c.code}/registration`}>{c.name}</Link> <ResponsiblePersons persons={c.responsiblePersons} /></td> : <td>{c.name}</td>}
                <td className='text-center'>{`${formatInt(c.counts.filtered)} of ${formatInt(c.counts.total)}`}</td>
                <td>{formatInt(c.registration.rollAll)}</td>
                <td>{formatInt(c.registration.roll7Days)}</td>
                <td>{formatInt(c.registration.roll30Days)}</td>
                <td>{formatInt(c.registration.rollWeeklyAvg)}</td>
                <td>{formatInt(c.registration.rollWeeklyAvgRequired)}</td>
                <td className='text-center'>{formatInt(c.registration.milestone)}</td>
                <td style={statsStyle(c.registration.rollAll, c.registration.milestone)} className='text-center'>{formatPerc(c.registration.rollAll / c.registration.milestone)}</td>
                <td>{c.registration.target ? formatInt(c.registration.target) : 'No Target Set'}</td>
                <td style={statsStyle(c.registration.rollAll, c.registration.target)} className='text-center'>{formatPerc(c.registration.rollAll / c.registration.target)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className='font-weight-bold'>
              <td>Total</td>
              <td className='text-center'>{`${formatInt(stats.counts.filtered)} of ${formatInt(stats.counts.total)}`}</td>
              <td className='text-center'>{formatInt(stats.registration.rollAll)}</td>
              <td className='text-center'>{formatInt(stats.registration.roll7Days)}</td>
              <td className='text-center'>{formatInt(stats.registration.roll30Days)}</td>
              <td className='text-center'>{formatInt(stats.registration.rollWeeklyAvg)}</td>
              <td className='text-center'>{formatInt(stats.registration.rollWeeklyAvgRequired)}</td>
              <td className='text-center'>{formatInt(stats.registration.milestone)}</td>
              <td style={statsStyle(stats.registration.rollAll, stats.registration.milestone)} className='text-center'>{formatPerc(stats.registration.rollAll / stats.registration.milestone)}</td>
              <td className='text-center'>{formatInt(stats.registration.target)}</td>
              <td style={statsStyle(stats.registration.rollAll, stats.registration.target)} className='text-center'>{formatPerc(stats.registration.rollAll / stats.registration.target)}</td>
            </tr>
          </tfoot>
        </Table>
      </>
    )
  }
}
